import { useState, useMemo } from 'react';
import { AddIcon } from '@trading-ui/icons';
import { TradingButton, TradingText } from '@trading-ui/core';
import { useTranslation } from 'react-i18next';

import type { Plant } from '@/interfaces';

import useAppStore from '@/store/app.store';
import { ROLES } from '@/constants/roles';

import { CompanyCupsTable, CompanyUsersTable } from '../../tables';

import {
  CreateCompanyCupsModal,
  UpdateCompanyCupsModal,
  DeleteCompanyCupsModal,
  UpdateCompanyUsersModal,
  CreateCompanyUsersModal,
} from '../../modals';

import { getCompanyCupsColumns } from '../../tables/company-cups-table/constants/columns';
import { getCompanyUsersColumns } from '../../tables/company-users-table/constants/columns';

import useSettingsStore from '../../../store/useSettingsStore';
import useCompanyDetails from '../../../hooks/api/useCompanyDetails';
import usePricingIndices from '../../../hooks/api/usePricingIndices';

import * as styles from './CompanySelectedPanel.css';

type Props = {
  companyId: number;
};

export default function CompanySelectedPanel({ companyId }: Props) {
  const { t } = useTranslation();

  const user = useAppStore((state) => state.user);
  const isManager = user?.role === ROLES.MANAGER;

  const isOpenCompanyCupsModal = useSettingsStore((state) => state.isOpenCompanyCupsModal);
  const setIsOpenCompanyCupsModal = useSettingsStore((state) => state.setIsOpenCompanyCupsModal);
  const isOpenCompanyUsersModal = useSettingsStore((state) => state.isOpenCompanyUsersModal);
  const setIsOpenCompanyUsersModal = useSettingsStore((state) => state.setIsOpenCompanyUsersModal);
  const selectedCups = useSettingsStore((state) => state.selectedCups);
  const setSelectedCups = useSettingsStore((state) => state.setSelectedCups);
  const selectedUser = useSettingsStore((state) => state.selectedCompanyUser);
  const setSelectedUser = useSettingsStore((state) => state.setSelectedCompanyUser);

  const [cupsToDelete, setCupsToDelete] = useState<Plant | null>(null);

  const {
    companyUsers,
    companyPlants,
    isLoading,
    isRefetching,
    deletingCupsId,
    deletingUserId,
    handleCreatePlant,
    handleUpdatePlant,
    handleDeletePlant,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
  } = useCompanyDetails(companyId);

  const { pricingIndices } = usePricingIndices();

  const cupsColumns = useMemo(
    () =>
      getCompanyCupsColumns({
        setSelectedCups,
        setCupsToDelete,
        deletingId: deletingCupsId,
        pricingIndices,
      }),
    [setSelectedCups, deletingCupsId, pricingIndices]
  );

  const usersColumns = useMemo(
    () =>
      getCompanyUsersColumns({
        setSelectedUser,
        handleDeleteUser,
        deletingId: deletingUserId,
      }),
    [setSelectedUser, handleDeleteUser, deletingUserId]
  );

  return (
    <>
      <div className={styles.tableContainer}>
        <div className={styles.tableTitle}>
          <TradingText text={t('settings.companyDetailsTab.companyCups.title')} variant="h6" />
          <TradingButton
            label={t('settings.companyDetailsTab.companyCups.addCupsButton')}
            handleClick={() => setIsOpenCompanyCupsModal(true)}
            startIcon={<AddIcon size={16} />}
            disabled={isLoading}
          />
        </div>
        <CompanyCupsTable
          columns={cupsColumns}
          data={companyPlants}
          isLoading={isLoading}
          isRefetching={isRefetching}
          isManager={isManager}
        />
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.tableTitle}>
          <TradingText text={t('settings.companyDetailsTab.companyUsers.title')} variant="h6" />
          {/* <TradingButton
            label={t('settings.companyDetailsTab.companyUsers.addUsersButton')}
            handleClick={() => setIsOpenCompanyUsersModal(true)}
            startIcon={<AddIcon size={16} />}
            disabled={isLoading}
          /> */}
        </div>
        <CompanyUsersTable
          columns={usersColumns}
          data={companyUsers}
          isLoading={isLoading}
          isRefetching={isRefetching}
          isManager={isManager}
        />
      </div>
      {isOpenCompanyCupsModal && (
        <CreateCompanyCupsModal
          setIsOpen={setIsOpenCompanyCupsModal}
          companyUsers={companyUsers}
          pricingIndices={pricingIndices}
          handleCreatePlant={handleCreatePlant}
          companyId={companyId}
        />
      )}
      {isOpenCompanyUsersModal && (
        <CreateCompanyUsersModal
          setIsOpen={setIsOpenCompanyUsersModal}
          companyPlants={companyPlants}
          handleCreateUser={handleCreateUser}
          companyId={companyId}
        />
      )}
      {selectedCups && (
        <UpdateCompanyCupsModal
          selectedCups={selectedCups}
          setSelectedCups={setSelectedCups}
          companyUsers={companyUsers}
          pricingIndices={pricingIndices}
          handleUpdatePlant={handleUpdatePlant}
          companyId={companyId}
        />
      )}
      {selectedUser && (
        <UpdateCompanyUsersModal
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          companyPlants={companyPlants}
          handleUpdateUser={handleUpdateUser}
          companyId={companyId}
        />
      )}
      {cupsToDelete && (
        <DeleteCompanyCupsModal
          cupsToDelete={cupsToDelete}
          setCupsToDelete={setCupsToDelete}
          handleDeletePlant={handleDeletePlant}
          isDeleting={deletingCupsId === cupsToDelete.id}
        />
      )}
    </>
  );
}
